import { useState, useRef, useEffect } from 'react';
import { LayoutGrid, Box, Sparkles, Lock, Check, ExternalLink, Users } from 'lucide-react';

/**
 * Peter's Universe — global App Switcher
 *
 * Shows the active module (Shoebox3D) + future locked modules (Jarvis IA Majordome,
 * etc.) in a floating dropdown anchored to a button in the top-bar. Designed to be
 * dropped into both the Landing Page and the Hub Dashboard.
 *
 * Props
 *  - theme: 'dark' | 'smooth' — controls visual tokens
 *  - testid: optional override for the trigger data-testid
 */
const MODULES = [
  {
    id: 'shoebox3d',
    name: 'Shoebox3D',
    tagline: 'Cartographie 3D immobilière',
    icon: Box,
    status: 'active',
    href: '/studio/dashboard',
  },
  {
    id: 'crm',
    name: 'Mini-CRM',
    tagline: 'Prospects, opportunités, pipeline',
    icon: Users,
    status: 'active',
    href: '/crm',
  },
  // V14.68.17 — Jarvis teaser archivé (preview permanente sans utilisation).
  {
    id: 'placeholder',
    name: 'Module Σ',
    tagline: 'À définir — vos idées bienvenues',
    icon: LayoutGrid,
    status: 'backlog',
    href: null,
  },
];

const AppSwitcher = ({ theme = 'dark', testid = 'app-switcher-btn' }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const isSmooth = theme === 'smooth';

  return (
    <div ref={wrapperRef} className="relative" data-testid="app-switcher-wrapper">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        aria-label="Peter's Universe — Modules"
        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all border ${
          open
            ? 'bg-[#D9A05B]/20 border-[#D9A05B]/40 text-[#D9A05B]'
            : isSmooth
              ? 'bg-white/55 border-black/10 text-black/65 hover:bg-white/80 backdrop-blur-md'
              : 'bg-white/5 border-white/10 text-white/65 hover:bg-white/10 backdrop-blur-md'
        }`}
      >
        <LayoutGrid className="w-4 h-4" />
      </button>

      {open && (
        <div
          className={`absolute right-0 top-12 z-[1500] w-[300px] rounded-2xl shadow-2xl overflow-hidden border ${
            isSmooth
              ? 'bg-white/90 border-black/10 text-black backdrop-blur-2xl'
              : 'bg-[#0d0d10]/95 border-white/10 text-white backdrop-blur-2xl'
          }`}
          data-testid="app-switcher-panel"
          role="menu"
        >
          {/* Header */}
          <div
            className={`px-4 py-3 border-b flex items-center gap-2.5 ${
              isSmooth ? 'border-black/8 bg-black/[0.02]' : 'border-white/8 bg-white/[0.02]'
            }`}
          >
            <div className="w-7 h-7 rounded-lg bg-[#D9A05B] flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${isSmooth ? 'text-black' : 'text-white'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                Peter's Universe
              </p>
              <p className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isSmooth ? 'text-black/45' : 'text-white/45'}`}>
                Écosystème · Modules
              </p>
            </div>
          </div>

          {/* Module list */}
          <div className="p-1.5">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const isActive = m.status === 'active';
              const isPreview = m.status === 'preview';
              const isLocked = !isActive && !isPreview;
              const isClickable = isActive || isPreview;

              const baseRow = `flex items-center gap-3 px-3 py-2.5 rounded-xl text-left w-full transition-all`;
              const activeRow = isSmooth
                ? `bg-[#D9A05B]/15 border border-[#D9A05B]/30 hover:bg-[#D9A05B]/20`
                : `bg-[#D9A05B]/10 border border-[#D9A05B]/25 hover:bg-[#D9A05B]/15`;
              const previewRow = isSmooth
                ? `bg-white/40 border border-[#D9A05B]/20 hover:bg-[#D9A05B]/10 hover:border-[#D9A05B]/40`
                : `bg-white/[0.03] border border-[#D9A05B]/15 hover:bg-[#D9A05B]/8 hover:border-[#D9A05B]/35`;
              const lockedRow = isSmooth
                ? `border border-transparent opacity-50 cursor-not-allowed`
                : `border border-transparent opacity-40 cursor-not-allowed`;

              const rowClasses = isActive ? activeRow : (isPreview ? previewRow : lockedRow);

              const content = (
                <>
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      isActive
                        ? 'bg-[#D9A05B]'
                        : isPreview
                          ? (isSmooth ? 'bg-[#D9A05B]/15 border border-[#D9A05B]/30' : 'bg-[#D9A05B]/15 border border-[#D9A05B]/25')
                          : (isSmooth ? 'bg-black/5' : 'bg-white/5')
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${
                      isActive
                        ? 'text-black'
                        : isPreview
                          ? 'text-[#D9A05B]'
                          : (isSmooth ? 'text-black/55' : 'text-white/55')
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className={`text-sm font-semibold truncate ${isSmooth ? 'text-black' : 'text-white'}`}>
                        {m.name}
                      </p>
                      {isActive && (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500 border border-emerald-500/30">
                          Actif
                        </span>
                      )}
                      {isPreview && (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#D9A05B]/15 text-[#D9A05B] border border-[#D9A05B]/30">
                          Preview
                        </span>
                      )}
                    </div>
                    <p className={`text-[11px] truncate mt-0.5 ${isSmooth ? 'text-black/50' : 'text-white/45'}`}>
                      {m.tagline}
                    </p>
                  </div>
                  {isActive && <Check className="w-4 h-4 text-[#D9A05B] shrink-0" />}
                  {isPreview && <Sparkles className="w-3.5 h-3.5 text-[#D9A05B] shrink-0" />}
                  {isLocked && <Lock className="w-3.5 h-3.5 shrink-0 opacity-60" />}
                </>
              );

              return isClickable ? (
                <a
                  key={m.id}
                  href={m.href}
                  className={`${baseRow} ${rowClasses}`}
                  data-testid={`module-${m.id}`}
                  role="menuitem"
                >
                  {content}
                </a>
              ) : (
                <div
                  key={m.id}
                  className={`${baseRow} ${rowClasses}`}
                  data-testid={`module-${m.id}`}
                  aria-disabled="true"
                  role="menuitem"
                >
                  {content}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div
            className={`px-4 py-2.5 border-t flex items-center justify-between gap-2 text-[10px] uppercase tracking-widest font-bold ${
              isSmooth ? 'border-black/8 text-black/40 bg-black/[0.02]' : 'border-white/8 text-white/40 bg-white/[0.02]'
            }`}
          >
            <span>orchestration · IA Majordome</span>
            <ExternalLink className="w-3 h-3 opacity-50" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AppSwitcher;
